import React, { useCallback, useEffect, useState } from 'react';
import { HiOutlineBell, HiOutlineX } from 'react-icons/hi';
import {
  getPushPermissionState,
  requestPushPermissionAndRegister,
  warmUpPushServiceWorker,
} from '../services/webPushService';

const DISMISS_KEY = 'gopai_push_prompt_dismissed';

/**
 * Edge·Chrome 등: Notification.requestPermission()은 사용자 클릭 없이 호출하면
 * 권한 창이 뜨지 않음. 로그인 후 배너에서 명시적으로 허용 요청.
 */
export default function PushNotificationPrompt() {
  const [permission, setPermission] = useState(() => getPushPermissionState());
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [loading, setLoading] = useState(false);

  const refreshPermission = useCallback(() => {
    setPermission(getPushPermissionState());
  }, []);

  useEffect(() => {
    void warmUpPushServiceWorker();
    refreshPermission();
  }, [refreshPermission]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const result = await requestPushPermissionAndRegister();
      refreshPermission();
      if (result === 'registered') {
        setDismissed(true);
        try {
          sessionStorage.setItem(DISMISS_KEY, '1');
        } catch {
          // ignore
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
  };

  if (permission === 'unsupported' || permission === 'granted' || dismissed) {
    return null;
  }

  if (permission === 'denied') {
    return (
      <div className="mx-6 mt-4 mb-0 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <HiOutlineBell className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="flex-1">
          <p className="font-medium">브라우저 알림이 차단되어 있습니다.</p>
          <p className="mt-1 text-amber-800">
            주소창 왼쪽 자물쇠(또는 사이트 정보) → 알림 → 허용으로 변경한 뒤 페이지를 새로고침해 주세요.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 text-amber-700 hover:text-amber-900"
          aria-label="닫기"
        >
          <HiOutlineX className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="mx-6 mt-4 mb-0 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
      <HiOutlineBell className="h-5 w-5 shrink-0 text-blue-600" />
      <div className="flex-1 min-w-[200px] space-y-1">
        <p>채팅·설계·청약 알림을 받으려면 브라우저 알림을 허용해 주세요.</p>
        <p>
          브라우저 알림과 별도로 PC 시스템 설정에서의 알림도 켜져 있어야 하며 그렇지 않은 경우 사용자가 설정에서 직접 알림을 허용해 주세요.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          disabled={loading}
          onClick={handleEnable}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? '처리 중…' : '알림 허용'}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded-md px-2 py-1.5 text-sm text-blue-700 hover:bg-blue-100"
        >
          나중에
        </button>
      </div>
    </div>
  );
}
